import { AlkLogger } from '@alk/core';
import { collectNotificationsPool } from '../notifications/notifications.service.utils';
import { concat, merge, Observable, Subject } from 'rxjs';
import { first, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { inject, injectable, multiInject } from 'inversify';
import {
    INotificationItem,
    INotificationMutationService,
    ISessionDisposable,
    NotificationMessagesListTransaction,
} from '../../../interfaces';
import { INotificationsPoolService } from './notifications-pool.interface';
import { INotificationsService, NOTIFICATIONS_SERVICE } from '../notifications';
import { mutationsToTransactionNotifications } from './notifications-pool.utils';
import { NOTIFICATION_MUTATION_SERVICE } from '../..';
import { typedGet } from '../../../helpers/object-utils';

@injectable()
export class NotificationsPoolService implements INotificationsPoolService, ISessionDisposable {

    @inject(NOTIFICATIONS_SERVICE) private readonly notificationsService: INotificationsService;

    @multiInject(NOTIFICATION_MUTATION_SERVICE) private readonly mutationServices: Array<INotificationMutationService>;

    private notificationsPool$: Observable<Array<INotificationItem>>;

    private mutationsObs$: Observable<NotificationMessagesListTransaction>;

    private readonly destroy$ = new Subject<boolean>();

    notificationsPoolObservable(): Observable<Array<INotificationItem>> {
        return this.notificationsPool$ || (this.notificationsPool$ = this.createPool());
    }

    stop(): void {

        AlkLogger.debug('[NotificationsPoolService#stop] notifications pool is about to stop');

        this.destroy$.next(true);

        delete this.notificationsPool$;
    }

    private createPool(): Observable<Array<INotificationItem>>{

        AlkLogger.debug('[NotificationsPoolService#createPool] notifications pool is about to create');

        return concat(
            this.notificationsService.notificationTransactionsObservable().pipe(first()),
            merge(
                this.notificationsService.notificationTransactionsObservable(),
                this.getMutationsObs(),
            ),
        ).pipe(
            collectNotificationsPool(),
            takeUntil(this.destroy$),
            shareReplay(1),
        );
    }

    private readonly getMutationsObs = (): Observable<NotificationMessagesListTransaction> => {
        if (Boolean(this.mutationsObs$)) {
            return this.mutationsObs$;
        }
        this.mutationsObs$ = merge(
            ...this.mutationServices.map(service => service.mutations()),
        ).pipe(
            tap(notifications =>
                AlkLogger.debug(`[NotificationsPoolService] mutation ${ notifications?.map(typedGet('id')) }`)),
            map(mutationsToTransactionNotifications),
            takeUntil(this.destroy$),
            shareReplay(),
        );

        this.mutationsObs$.subscribe().unsubscribe();

        return this.mutationsObs$;
    };
}
